import { z } from "zod";
import type { PatientGroup } from "./patient-group";
import {
  deriveOnboardingStatus,
  onboardingActivities,
  DECISION_APPROVED_LABEL,
  ONBOARDING_STATUS_LABEL,
  onboardingStatusKey,
  type FormProgressFacts,
  type OnboardingFacts,
  type OnboardingStatusKey,
} from "./onboarding-status";

// TES-1117 S2 (Emelie 2026-10-05, decision 4) — the timeline the onboarding
// card opens: "status and date for F-01, identity/e-mail, app handoff and app
// login, F-02B, F-07, submitted documentation, doctor review, any
// completion, the final decision, subscription activation, the prescription,
// and treatment start."
//
// Built from the same OnboardingFacts as the precise status (S1), so the
// card's status and its timeline cannot disagree. Pure; the API runs it.
//
// RULES
// - The steps come in one fixed order (the journey's order), not sorted by
//   date: a late F-01 resubmission must not move F-01 below the decision.
// - A step that does not apply to her journey is left out (F-07 and the
//   transfer review for a new treatment; a komplettering she never had; the
//   steps after a negative decision).
// - A step not reached yet is `pending` and carries no date.
//
// S3's journey funnel counts these same steps, so "reached" means the same
// thing on the card and in the funnel.

export const ONBOARDING_TIMELINE_STEPS = [
  "f01",
  "account",
  "email_verified",
  "app_handoff",
  "app_login",
  "declaration",
  "f07",
  "documentation_submitted",
  "doctor_review",
  "meeting_booked",
  "meeting_held",
  "completion",
  "decision",
  "subscription",
  "prescription",
  "treatment_start",
] as const;
export const onboardingTimelineStepKeySchema = z.enum(
  ONBOARDING_TIMELINE_STEPS,
);
export type OnboardingTimelineStepKey = z.infer<
  typeof onboardingTimelineStepKeySchema
>;

export const ONBOARDING_TIMELINE_STATES = [
  "done",
  "in_progress",
  "pending",
] as const;
export type OnboardingTimelineState =
  (typeof ONBOARDING_TIMELINE_STATES)[number];

export const onboardingTimelineStepSchema = z.object({
  key: onboardingTimelineStepKeySchema,
  /** The step's name, in Swedish. */
  label: z.string(),
  state: z.enum(ONBOARDING_TIMELINE_STATES),
  /** A short Swedish qualifier ("Utkast sparat", "Godkänd …"), or null. */
  note: z.string().nullable(),
  /** When it was started (ISO), where that differs from `at`; else null. */
  startedAt: z.string().nullable(),
  /**
   * The step's date (ISO): when it was done, or the latest activity on a
   * step in progress. Always null on a pending step; null on a done step
   * only when nothing dated is stored for it.
   */
  at: z.string().nullable(),
});
export type OnboardingTimelineStep = z.infer<
  typeof onboardingTimelineStepSchema
>;

// Step names. Emelie's own words from the ticket's funnel ("BankID/konto",
// "Underlag inskickat", "Läkargranskning", "Slutligt behandlingsbeslut",
// "Abonnemang aktiverat", "Recept utfärdat/behandling startad" split in
// two) and S1's accepted activity labels (Ö-343). "Appöverlämning" /
// "Appinloggning" split her "appöverlämning och appinloggning"; "Komplettering"
// is her "eventuell komplettering".
function stepLabel(
  key: OnboardingTimelineStepKey,
  journey: PatientGroup,
): string {
  switch (key) {
    case "f01":
      return "F-01";
    case "account":
      return "BankID/konto";
    case "email_verified":
      return "E-post bekräftad";
    case "app_handoff":
      return "Appöverlämning";
    case "app_login":
      return "Appinloggning";
    case "declaration":
      return journey === "PRESCRIPTION_TRANSFER" ? "F-02B" : "Hälsodeklaration";
    case "f07":
      return "F-07";
    case "documentation_submitted":
      return "Underlag inskickat";
    case "doctor_review":
      return "Läkargranskning";
    case "meeting_booked":
      return "Vårdmöte bokat";
    case "meeting_held":
      return "Vårdmöte genomfört";
    case "completion":
      return "Komplettering";
    case "decision":
      return "Slutligt behandlingsbeslut";
    case "subscription":
      return "Abonnemang aktiverat";
    case "prescription":
      return "Recept utfärdat";
    case "treatment_start":
      return "Behandling startad";
  }
}

// TES-1117 S2 qualifiers. Emelie approved them in Ö-353 (2026-10-06), with
// "Inväntar läkarbedömning" in place of the proposed "Inväntar läkaren".
const NOTE = {
  draftSaved: "Utkast sparat",
  submitted: "Inskickad",
  awaitingDoctor: "Inväntar läkarbedömning",
  completionRequested: "Begärd",
  completionReady: "Klar",
  linkSent: "Abonnemangslänk skickad",
} as const;

type Draft = {
  key: OnboardingTimelineStepKey;
  state: OnboardingTimelineState;
  note?: string | null;
  startedAt?: Date | null;
  at?: Date | null;
};

const iso = (d: Date | null | undefined) => (d ? d.toISOString() : null);

function earliestOf(...dates: Array<Date | null | undefined>): Date | null {
  let out: Date | null = null;
  for (const d of dates) if (d && (!out || d < out)) out = d;
  return out;
}

/** A form step: not started / draft saved / submitted. */
function formStep(key: OnboardingTimelineStepKey, f: FormProgressFacts): Draft {
  if (f.firstSubmittedAt) {
    return {
      key,
      state: "done",
      note: NOTE.submitted,
      startedAt: f.draftStartedAt,
      at: f.firstSubmittedAt,
    };
  }
  if (f.draftStartedAt) {
    return {
      key,
      state: "in_progress",
      note: NOTE.draftSaved,
      startedAt: f.draftStartedAt,
      at: f.draftSavedAt ?? f.draftStartedAt,
    };
  }
  return { key, state: "pending" };
}

const done = (key: OnboardingTimelineStepKey, at: Date | null): Draft =>
  at ? { key, state: "done", at } : { key, state: "pending" };

// The review statuses in which the doctor has acted on the documentation.
const REVIEWED = new Set([
  "completion_requested",
  "labs_required",
  "meeting_required",
  "approved",
  "cannot_transfer",
]);

export function buildOnboardingTimeline(
  f: OnboardingFacts,
): OnboardingTimelineStep[] {
  const transfer = f.journey === "PRESCRIPTION_TRANSFER";
  const status = onboardingStatusKey(f);
  // Patients started before TES-1069 have no prescription confirmation or
  // first-dose event to read; their treatment is a fact without a date.
  const legacyStarted =
    status === "treatment_started" && !f.prescriptionConfirmedAt;
  const steps: Draft[] = [];

  steps.push(done("f01", f.f01At));
  // The patient row is made by her first completed BankID.
  steps.push({ key: "account", state: "done", at: f.accountCreatedAt });
  steps.push(done("email_verified", f.emailVerifiedAt));

  // A form draft or submission is made in the app, so it proves the login
  // on app versions without the install signal (as in S1).
  const appLoginAt = earliestOf(
    f.appLoginAt,
    f.declaration.draftStartedAt,
    f.declaration.firstSubmittedAt,
    f.transferForm.draftStartedAt,
    f.transferForm.firstSubmittedAt,
  );
  // Only a web start has a hand-off; someone who began in the app skips it.
  if (f.appHandoffAt || !appLoginAt) {
    steps.push(done("app_handoff", f.appHandoffAt));
  }
  steps.push(done("app_login", appLoginAt));

  steps.push(formStep("declaration", f.declaration));
  if (transfer) {
    steps.push(formStep("f07", f.transferForm));

    const review = f.transferReview;
    steps.push(done("documentation_submitted", review?.createdAt ?? null));
    if (review && (REVIEWED.has(review.status) || f.decision !== "none")) {
      // No "opened by the doctor" time is stored; the review's own outcome
      // times are the nearest facts.
      steps.push({
        key: "doctor_review",
        state: "done",
        note:
          review.status === "meeting_required"
            ? ONBOARDING_STATUS_LABEL.transfer_meeting_required
            : null,
        at: review.completionRequestedAt ?? review.decidedAt ?? null,
      });
    } else if (review) {
      steps.push({
        key: "doctor_review",
        state: "in_progress",
        note: NOTE.awaitingDoctor,
        at: review.createdAt,
      });
    } else {
      steps.push({ key: "doctor_review", state: "pending" });
    }
  }

  // A transfer patient meets the doctor only when the review asks for it.
  const bookedAt = f.firstMeetingBookedAt ?? f.meetingBooked?.bookedAt ?? null;
  const hasMeeting =
    !transfer ||
    bookedAt !== null ||
    f.meetingHeldAt !== null ||
    f.transferReview?.status === "meeting_required";
  if (hasMeeting) {
    steps.push(
      bookedAt || !f.meetingHeldAt
        ? done("meeting_booked", bookedAt)
        : { key: "meeting_booked", state: "done", at: null },
    );
    steps.push(done("meeting_held", f.meetingHeldAt));
  }

  // "Eventuell komplettering": shown only when one was asked for.
  const round = f.completionRound;
  const reviewCompletionAt = f.transferReview?.completionRequestedAt ?? null;
  if (round?.status === "open") {
    steps.push({
      key: "completion",
      state: "in_progress",
      note: NOTE.completionRequested,
      at: round.requestedAt,
    });
  } else if (round?.status === "ready") {
    steps.push({
      key: "completion",
      state: "done",
      note: NOTE.completionReady,
      startedAt: round.requestedAt,
      at: round.readyAt ?? round.requestedAt,
    });
  } else if (reviewCompletionAt) {
    const open = f.transferReview?.status === "completion_requested";
    steps.push({
      key: "completion",
      state: open ? "in_progress" : "done",
      note: open ? NOTE.completionRequested : null,
      startedAt: open ? null : reviewCompletionAt,
      at: open ? reviewCompletionAt : null,
    });
  }

  if (f.decision === "none") {
    steps.push(
      legacyStarted
        ? { key: "decision", state: "done", at: null }
        : { key: "decision", state: "pending" },
    );
  } else {
    steps.push({
      key: "decision",
      state: "done",
      note:
        f.decision === "approved"
          ? DECISION_APPROVED_LABEL
          : transfer
            ? ONBOARDING_STATUS_LABEL.transfer_declined
            : ONBOARDING_STATUS_LABEL.new_declined,
      at: f.decisionAt,
    });
  }

  // Nothing follows a negative decision.
  if (f.decision !== "declined") {
    if (f.subscriptionActivatedAt) {
      steps.push(done("subscription", f.subscriptionActivatedAt));
    } else if (f.subscriptionLinkSentAt) {
      steps.push({
        key: "subscription",
        state: "in_progress",
        note: NOTE.linkSent,
        at: f.subscriptionLinkSentAt,
      });
    } else if (legacyStarted) {
      steps.push({ key: "subscription", state: "done", at: null });
    } else {
      steps.push({ key: "subscription", state: "pending" });
    }
    steps.push(
      legacyStarted
        ? { key: "prescription", state: "done", at: null }
        : // TES-1150 — issued at the doctor's registration, before the
          // subscription; the release is a later, separate fact.
          done(
            "prescription",
            f.prescriptionRegisteredAt ?? f.prescriptionConfirmedAt,
          ),
    );
    steps.push(
      f.firstDoseAt || !legacyStarted
        ? done("treatment_start", f.firstDoseAt)
        : { key: "treatment_start", state: "done", at: null },
    );
  }

  return steps.map((s) => ({
    key: s.key,
    label: stepLabel(s.key, f.journey),
    state: s.state,
    note: s.state === "pending" ? null : (s.note ?? null),
    startedAt: s.state === "pending" ? null : iso(s.startedAt),
    at: s.state === "pending" ? null : iso(s.at),
  }));
}

// TES-1209 — the coach's copy of the timeline. A-01 has the coach and the
// admin see the same precise step ("coach och administratör ska samtidigt se
// patientens precisa aktuella steg"), but the doctor's review and decision
// reach the coach as a neutral status only, never as their content. So on
// those two steps an outcome qualifier ("Godkänd för behandling", "Vårdmöte
// krävs", "Ej godkänd för behandling") is dropped and the step keeps its
// state and date. A qualifier that only says we are waiting ("Inväntar
// läkarbedömning", on a step in progress) names no outcome and stays.
const DOCTOR_OUTCOME_STEPS: ReadonlySet<OnboardingTimelineStepKey> = new Set([
  "doctor_review",
  "decision",
]);

export function coachSafeOnboardingTimeline(
  steps: readonly OnboardingTimelineStep[],
): OnboardingTimelineStep[] {
  return steps.map((s) =>
    DOCTOR_OUTCOME_STEPS.has(s.key) && s.state !== "in_progress"
      ? { ...s, note: null }
      : s,
  );
}

// ── S3: the funnel per journey ─────────────────────────────────────────────
//
// Emelie's transfer funnel, verbatim: "F-01 → BankID/konto → appöverlämning
// och appinloggning → F-02B påbörjad/klar → F-07 påbörjad/klar → underlag
// inskickat → läkargranskning → eventuell komplettering → slutligt
// behandlingsbeslut → abonnemang aktiverat → recept utfärdat/behandling
// startad." Her "påbörjad/klar" steps are two counts each.
//
// The new-treatment funnel follows the same timeline past the booked meeting
// to the decision, the subscription and the prescription. Its first steps
// (BankID, e-mail, the app transition, booking) stay in the TES-1096 cohort
// table above it.
//
// A step is reached when its timeline step is (for "påbörjad", at least in
// progress; otherwise) done. "Eventuell komplettering" is conditional: it is
// counted, but only a patient the doctor asks for one has to pass it, so it
// never decides where anyone stands (Emelie, Ö-353: "Villkorsstyrt steg –
// räknas inte som avhopp"; since Ö-355 "inaktiv 7+ dagar").

export const JOURNEY_FUNNEL_STEP_KEYS = [
  "f01",
  "account",
  "app",
  "declaration_started",
  "declaration_done",
  "f07_started",
  "f07_done",
  "documentation_submitted",
  "doctor_review",
  "meeting_booked",
  "meeting_held",
  "completion",
  "decision",
  "subscription",
  "prescription",
] as const;
export const journeyFunnelStepKeySchema = z.enum(JOURNEY_FUNNEL_STEP_KEYS);
export type JourneyFunnelStepKey = z.infer<typeof journeyFunnelStepKeySchema>;

export const JOURNEY_FUNNEL_STEPS: Record<
  PatientGroup,
  readonly JourneyFunnelStepKey[]
> = {
  PRESCRIPTION_TRANSFER: [
    "f01",
    "account",
    "app",
    "declaration_started",
    "declaration_done",
    "f07_started",
    "f07_done",
    "documentation_submitted",
    "doctor_review",
    "completion",
    "decision",
    "subscription",
    "prescription",
  ],
  NEW_TREATMENT: [
    "f01",
    "account",
    "meeting_booked",
    "meeting_held",
    "completion",
    "decision",
    "subscription",
    "prescription",
  ],
};

/** Steps nobody has to pass; they never decide where she stands. */
export const OPTIONAL_JOURNEY_FUNNEL_STEPS: ReadonlySet<JourneyFunnelStepKey> =
  new Set(["completion"]);

/** Emelie's step names (transfer) and S1's accepted labels (new treatment). */
export const JOURNEY_FUNNEL_STEP_LABEL: Record<JourneyFunnelStepKey, string> = {
  f01: "F-01",
  account: "BankID/konto",
  app: "Appöverlämning och appinloggning",
  declaration_started: "F-02B påbörjad",
  declaration_done: "F-02B klar",
  f07_started: "F-07 påbörjad",
  f07_done: "F-07 klar",
  documentation_submitted: "Underlag inskickat",
  doctor_review: "Läkargranskning",
  meeting_booked: "Vårdmöte bokat",
  meeting_held: "Vårdmöte genomfört",
  completion: "Eventuell komplettering",
  decision: "Slutligt behandlingsbeslut",
  subscription: "Abonnemang aktiverat",
  prescription: "Recept utfärdat/behandling startad",
};

/** The journey-funnel steps she has reached, from her timeline. */
export function journeyFunnelReached(
  journey: PatientGroup,
  timeline: OnboardingTimelineStep[],
): Set<JourneyFunnelStepKey> {
  const state = new Map(timeline.map((s) => [s.key, s.state]));
  const isDone = (k: OnboardingTimelineStepKey) => state.get(k) === "done";
  const begun = (k: OnboardingTimelineStepKey) =>
    state.has(k) && state.get(k) !== "pending";
  const reached: Record<JourneyFunnelStepKey, boolean> = {
    f01: isDone("f01"),
    account: isDone("account"),
    app: isDone("app_login"),
    declaration_started: begun("declaration"),
    declaration_done: isDone("declaration"),
    f07_started: begun("f07"),
    f07_done: isDone("f07"),
    documentation_submitted: isDone("documentation_submitted"),
    doctor_review: isDone("doctor_review"),
    meeting_booked: isDone("meeting_booked"),
    meeting_held: isDone("meeting_held"),
    completion: begun("completion"),
    decision: isDone("decision"),
    subscription: isDone("subscription"),
    prescription: isDone("prescription") || isDone("treatment_start"),
  };
  return new Set(JOURNEY_FUNNEL_STEPS[journey].filter((k) => reached[k]));
}

/**
 * Whether she stands AT `step`: the drill-down from a funnel step lists
 * these people, the ones the next move is waiting on.
 * - A required step: it is the furthest step she has reached.
 * - The optional komplettering: she has reached it and not the next
 *   required step.
 */
export function isAtJourneyFunnelStep(
  journey: PatientGroup,
  reached: ReadonlySet<JourneyFunnelStepKey>,
  step: JourneyFunnelStepKey,
): boolean {
  const order = JOURNEY_FUNNEL_STEPS[journey];
  const i = order.indexOf(step);
  if (i < 0 || !reached.has(step)) return false;
  const later = order.slice(i + 1);
  if (OPTIONAL_JOURNEY_FUNNEL_STEPS.has(step)) {
    const next = later.find((k) => !OPTIONAL_JOURNEY_FUNNEL_STEPS.has(k));
    return !next || !reached.has(next);
  }
  return !later.some((k) => reached.has(k));
}

// ── Ö-354: the journey funnel's period, and ongoing vs dropped out ────────
//
// Emelie, 2026-10-06: by default the funnel shows the patients whose journey
// STARTED in the last 30 days, with the choice "7 dagar / 30 dagar / 90 dagar
// / Alla". Her start is the TES-1096 cohort's (the earlier of her first
// linked lead and her patient row). The period only picks who is counted;
// each step still counts what she has reached by today.

export const JOURNEY_FUNNEL_PERIODS = ["7d", "30d", "90d", "all"] as const;
export const journeyFunnelPeriodSchema = z.enum(JOURNEY_FUNNEL_PERIODS);
export type JourneyFunnelPeriod = z.infer<typeof journeyFunnelPeriodSchema>;
export const DEFAULT_JOURNEY_FUNNEL_PERIOD: JourneyFunnelPeriod = "30d";

/** Emelie's choice, verbatim. */
export const JOURNEY_FUNNEL_PERIOD_LABEL: Record<JourneyFunnelPeriod, string> =
  {
    "7d": "7 dagar",
    "30d": "30 dagar",
    "90d": "90 dagar",
    all: "Alla",
  };

/** Stockholm days back from today, today included; null = no lower bound. */
export const JOURNEY_FUNNEL_PERIOD_DAYS: Record<
  JourneyFunnelPeriod,
  number | null
> = { "7d": 7, "30d": 30, "90d": 90, all: null };

// Emelie: patients still in an ongoing journey ("e.g. a transfer in
// progress, or an open completion") are shown as ongoing and not counted as
// drop-outs. Built from S1's status and activity dates:
//
// - finished: the onboarding is over (treatment started, declined, paused or
//   ended; S1's `inOnboarding` is false). Neither ongoing nor a drop-out.
// - ongoing: she has begun her journey's own part (for a transfer a saved
//   or submitted F-02B/F-07, for a new treatment a booked meeting) or is
//   anywhere after it (review, komplettering, blood test, decision,
//   subscription, prescription). Ongoing until the onboarding is over,
//   however long it takes: the next move may be the doctor's, and a
//   doctor's request still waiting on her is an open journey.
// - Before that (no F-01, F-01 only, or signed in without starting F-02B)
//   nothing ties her to the clinic yet. She is ongoing while her latest
//   onboarding activity is less than JOURNEY_IDLE_DAYS old, and dropped out
//   after that.
export const JOURNEY_PROGRESS = ["ongoing", "dropped_out", "finished"] as const;
export const journeyProgressSchema = z.enum(JOURNEY_PROGRESS);
export type JourneyProgress = z.infer<typeof journeyProgressSchema>;

/**
 * Staff labels, both Emelie's (Ö-355, verbatim: "Avhopp" becomes "Inaktiv 7+
 * dagar" everywhere). A finished journey gets no progress label: its row
 * shows its outcome (S1's status label: treatment started, declined, paused
 * or ended) instead.
 */
export const JOURNEY_PROGRESS_LABEL: Record<
  Exclude<JourneyProgress, "finished">,
  string
> = {
  ongoing: "Pågående",
  dropped_out: "Inaktiv 7+ dagar",
};

/**
 * Days without any onboarding activity before someone who has not begun her
 * journey's own part counts as dropped out. Seven, like the TES-1096
 * cohort's follow-up window and the active period of a begun form draft
 * (FORM_DRAFT_COMPLETION_PERIOD_DAYS).
 */
export const JOURNEY_IDLE_DAYS = 7;

const BEFORE_OWN_JOURNEY = new Set<OnboardingStatusKey>([
  "f01_not_started",
  "transfer_f01_done",
  "transfer_declaration_not_started",
  "new_f01_done",
]);

/** Where her journey stands as of `now`; see JOURNEY_PROGRESS. */
export function journeyProgress(
  f: OnboardingFacts,
  now: Date,
): JourneyProgress {
  const status = deriveOnboardingStatus(f);
  if (!status.inOnboarding) return "finished";
  if (!BEFORE_OWN_JOURNEY.has(status.status)) return "ongoing";
  const activities = onboardingActivities(f);
  const latest = activities[activities.length - 1]?.at ?? f.accountCreatedAt;
  const idleMs = now.getTime() - latest.getTime();
  return idleMs < JOURNEY_IDLE_DAYS * 86_400_000 ? "ongoing" : "dropped_out";
}
